import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { admin_apiConfig } from 'shared/config/config';
import { authFetch } from 'shared/api/authFetch';
import { getBranchPreviewChatSession } from 'shared/api/branchPreviewSession';
import { takeDryRun, useViewAsTarget } from 'shared/api/viewAsSession';
import { useCurrentUserAccess } from 'shared/session/useCurrentUserRole';
import { useUserOrgId } from 'shared/session';
import { getActionFailureMessage } from 'shared/utils/actionInvocationFailure';
import {
  elapsedMs,
  logViewLatency,
  logViewLatencyError,
  nowMs,
  utf8ByteLength,
} from '../utils/viewLatencyLogger';

const buildApiError = (err, fallbackMessage, status) => {
  const detail = err?.detail;
  const message = typeof detail === 'string'
    ? detail
    : detail?.message || err?.message || fallbackMessage;
  const apiError = new Error(message || fallbackMessage);
  if (detail && typeof detail === 'object') {
    apiError.detail = detail;
  }
  if (status) {
    apiError.status = status;
  }
  return apiError;
};

const responseHeader = (response, name) => response?.headers?.get?.(name) || null;

const isBrowserFile = (value) =>
  typeof File !== 'undefined' && value instanceof File;

const extractActionPayloadFiles = (value, path = '') => {
  if (isBrowserFile(value)) {
    return {
      payload: {
        kind: 'pending_file_ref',
        filename: value.name,
        content_type: value.type || 'application/octet-stream',
        size_bytes: value.size,
        last_modified_at: value.lastModified ? new Date(value.lastModified).toISOString() : undefined,
      },
      files: [{ path, file: value }],
    };
  }
  if (Array.isArray(value)) {
    const files = [];
    const payload = value.map((item, index) => {
      const extracted = extractActionPayloadFiles(item, path ? `${path}.${index}` : String(index));
      files.push(...extracted.files);
      return extracted.payload;
    });
    return { payload, files };
  }
  if (value && typeof value === 'object') {
    const files = [];
    const payload = {};
    for (const [key, nestedValue] of Object.entries(value)) {
      const extracted = extractActionPayloadFiles(nestedValue, path ? `${path}.${key}` : key);
      files.push(...extracted.files);
      payload[key] = extracted.payload;
    }
    return { payload, files };
  }
  return { payload: value, files: [] };
};

const fetchViewApi = async (path, meta = {}) => {
  const startedAt = nowMs();
  logViewLatency('backend.request.start', {
    path,
    ...meta,
  });
  const response = await authFetch(`${admin_apiConfig.baseURL}${path}`);
  const headersDurationMs = elapsedMs(startedAt);
  logViewLatency('backend.request.headers', {
    path,
    status: response.status,
    durationMs: headersDurationMs,
    responseContentLength: responseHeader(response, 'content-length'),
    responseContentType: responseHeader(response, 'content-type'),
    ...meta,
  });
  if (!response.ok) {
    const errorJsonStartedAt = nowMs();
    const err = await response.json().catch(() => ({ detail: response.statusText }));
    const apiError = buildApiError(err, `Request failed (${response.status})`, response.status);
    logViewLatencyError('backend.request.error', apiError, {
      path,
      status: response.status,
      durationMs: elapsedMs(startedAt),
      headersDurationMs,
      jsonParseMs: elapsedMs(errorJsonStartedAt),
      ...meta,
    });
    throw apiError;
  }
  const jsonStartedAt = nowMs();
  const payload = await response.json();
  const jsonParseMs = elapsedMs(jsonStartedAt);
  logViewLatency('backend.request.success', {
    path,
    status: response.status,
    durationMs: elapsedMs(startedAt),
    headersDurationMs,
    jsonParseMs,
    responseContentLength: responseHeader(response, 'content-length'),
    responsePayloadBytes: utf8ByteLength(payload),
    payloadKeys: payload && typeof payload === 'object' ? Object.keys(payload) : [],
    ...meta,
  });
  return payload;
};

const sendViewApiJson = async (path, { method = 'POST', body = {}, meta = {} } = {}) => {
  const startedAt = nowMs();
  logViewLatency('backend.request.start', {
    path,
    method,
    ...meta,
  });
  const response = await authFetch(`${admin_apiConfig.baseURL}${path}`, {
    method,
    body: JSON.stringify(body || {}),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: response.statusText }));
    const apiError = buildApiError(err, `Request failed (${response.status})`, response.status);
    logViewLatencyError('backend.request.error', apiError, {
      path,
      method,
      status: response.status,
      durationMs: elapsedMs(startedAt),
      ...meta,
    });
    throw apiError;
  }
  const payload = await response.json();
  logViewLatency('backend.request.success', {
    path,
    method,
    status: response.status,
    durationMs: elapsedMs(startedAt),
    responsePayloadBytes: utf8ByteLength(payload),
    payloadKeys: payload && typeof payload === 'object' ? Object.keys(payload) : [],
    ...meta,
  });
  return payload;
};

export const ONTOLOGY_VIEWS_QUERY_KEY = 'ontologyViewsRuntime';
export const SANDBOX_QUERY_KEY = 'sandbox';

export const sandboxRuntimeParams = (sandboxContext) => {
  if (!sandboxContext?.sandbox_id) return null;
  const params = new URLSearchParams();
  params.set('sandbox_id', sandboxContext.sandbox_id);
  [
    'active_overlay_id',
    'approval_id',
    'workspace_snapshot_key',
    'baseline_workspace_snapshot_key',
    'parent_environment_snapshot_id',
    'source_session_id',
  ].forEach((key) => {
    if (sandboxContext[key]) params.set(key, sandboxContext[key]);
  });
  return params;
};

const sameSandboxRevision = (previousKey, nextKey) => {
  if (!previousKey || !nextKey) return previousKey === nextKey;
  const previous = new URLSearchParams(previousKey);
  const next = new URLSearchParams(nextKey);
  return ['sandbox_id', 'active_overlay_id'].every((key) => previous.get(key) === next.get(key));
};

export const useOntologyViewsIndex = ({ enabled = true, sandboxContext = null } = {}) => {
  const { data: orgId } = useUserOrgId();
  const runtimeSandboxParams = sandboxRuntimeParams(sandboxContext);
  const sandboxKey = runtimeSandboxParams?.toString() || null;
  const viewAsTarget = useViewAsTarget();

  return useQuery({
    queryKey: [ONTOLOGY_VIEWS_QUERY_KEY, 'index', orgId || null, sandboxKey, viewAsTarget],
    enabled: Boolean(enabled && orgId),
    queryFn: async () => {
      const suffix = runtimeSandboxParams ? `?${runtimeSandboxParams.toString()}` : '';
      const payload = await fetchViewApi(`/api/ontology-views${suffix}`, {
        query: 'views-index',
        orgId,
        sandboxId: sandboxContext?.sandbox_id || null,
      });
      return payload?.views || [];
    },
    staleTime: 60 * 1000,
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey?.[3] === sandboxKey && previousQuery?.queryKey?.[4] === viewAsTarget ? previousData : undefined,
  });
};

export const useOntologyViewRuntime = ({ viewId, enabled = true, sandboxContext = null } = {}) => {
  const { data: orgId } = useUserOrgId();
  const runtimeSandboxParams = sandboxRuntimeParams(sandboxContext);
  const sandboxKey = runtimeSandboxParams?.toString() || null;
  const viewAsTarget = useViewAsTarget();

  return useQuery({
    queryKey: [ONTOLOGY_VIEWS_QUERY_KEY, 'runtime', orgId || null, viewId || null, sandboxKey, viewAsTarget],
    enabled: Boolean(enabled && orgId && viewId),
    queryFn: async () => {
      const suffix = runtimeSandboxParams ? `?${runtimeSandboxParams.toString()}` : '';
      const payload = await fetchViewApi(`/api/ontology-views/${viewId}/runtime${suffix}`, {
        query: 'view-runtime',
        orgId,
        viewId,
        sandboxId: sandboxContext?.sandbox_id || null,
      });
      return payload?.runtime || null;
    },
    staleTime: 30 * 1000,
    // A different revision must not display the previous revision's runtime.
    // A new snapshot hint for the same revision keeps the View on screen while
    // it refreshes; unmounting it would reload every source the View reads.
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey?.[3] === (viewId || null)
        && sameSandboxRevision(previousQuery?.queryKey?.[4], sandboxKey)
        && previousQuery?.queryKey?.[5] === viewAsTarget ? previousData : undefined,
  });
};

/** `watching`: someone is previewing a branch or choosing one, so changes show within seconds. */
export const useSandboxes = ({ enabled = true, chatSessionId = null, watching = false } = {}) => {
  const { data: orgId } = useUserOrgId();
  const { data: access } = useCurrentUserAccess();

  return useQuery({
    queryKey: [SANDBOX_QUERY_KEY, 'list', orgId || null, access?.profileId, chatSessionId],
    enabled: Boolean(enabled && orgId && access?.profileId),
    queryFn: async () => {
      const params = new URLSearchParams();
      const suffix = params.toString() ? `?${params.toString()}` : '';
      const payload = await fetchViewApi(`/api/ontology-views/sandboxes${suffix}`, {
        query: 'sandbox-list',
        orgId,
      });
      return payload?.sandboxes || [];
    },
    staleTime: 0,
    // Changes made through another chat or an MCP client share this picker. This tab's
    // own chats refresh it as they change it, and focusing the tab refetches it.
    refetchInterval: watching ? 5000 : 60000,
  });
};

export const useSandboxChanges = ({ sandboxId, approvalId = null, overlayId = null, preferCommitted = false, enabled = true } = {}) => {
  const { data: orgId } = useUserOrgId({ enabled });

  return useQuery({
    queryKey: [SANDBOX_QUERY_KEY, 'changes', orgId || null, sandboxId || null, approvalId, overlayId, preferCommitted, getBranchPreviewChatSession()],
    enabled: Boolean(enabled && orgId && sandboxId),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (approvalId) params.set('approval_id', approvalId);
      if (overlayId) params.set('overlay_id', overlayId);
      if (preferCommitted) params.set('prefer_committed', 'true');
      const payload = await fetchViewApi(
        `/api/ontology-views/sandboxes/${encodeURIComponent(sandboxId)}/changes${params.size ? `?${params}` : ''}`,
        {
          query: 'sandbox-changes',
          orgId,
          sandboxId,
        }
      );
      if (!payload?.changes) throw new Error('The branch review could not be loaded.');
      return payload.changes;
    },
    staleTime: 15 * 1000,
  });
};

export const useSandboxArtifacts = ({ sandboxId, overlayId = null, enabled = true } = {}) => {
  const { data: orgId } = useUserOrgId();

  return useQuery({
    queryKey: [
      SANDBOX_QUERY_KEY,
      'artifacts',
      orgId || null,
      sandboxId || null,
      overlayId || null,
      getBranchPreviewChatSession(),
    ],
    enabled: Boolean(enabled && orgId && sandboxId),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (overlayId) params.set('overlay_id', overlayId);
      const suffix = params.toString() ? `?${params.toString()}` : '';
      const payload = await fetchViewApi(
        `/api/ontology-views/sandboxes/${encodeURIComponent(sandboxId)}/artifacts${suffix}`,
        {
          query: 'sandbox-artifacts',
          orgId,
          sandboxId,
          overlayId,
        }
      );
      if (!Array.isArray(payload?.artifacts) || (overlayId && payload.overlay_id !== overlayId)) {
        throw new Error('The requested branch revision could not be loaded.');
      }
      return payload;
    },
    staleTime: 60 * 1000,
  });
};

export const useSandboxChangeRecords = ({ sandboxId, overlayId = null, approvalId = null, typeName, kind = 'object', limit = 25, offset = 0, enabled = true } = {}) => {
  const { data: orgId } = useUserOrgId();

  return useQuery({
    queryKey: [SANDBOX_QUERY_KEY, 'change-records', orgId || null, sandboxId || null, overlayId, approvalId, kind, typeName || null, limit, offset, getBranchPreviewChatSession()],
    enabled: Boolean(enabled && orgId && sandboxId && typeName),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('type', typeName);
      params.set('kind', kind);
      params.set('limit', String(limit));
      params.set('offset', String(offset));
      if (overlayId) params.set('overlay_id', overlayId);
      if (approvalId) params.set('approval_id', approvalId);
      const payload = await fetchViewApi(
        `/api/ontology-views/sandboxes/${encodeURIComponent(sandboxId)}/changes/records?${params.toString()}`,
        {
          query: 'sandbox-change-records',
          orgId,
          sandboxId,
        }
      );
      if (!Array.isArray(payload?.records)) throw new Error('The changed records could not be loaded.');
      return payload;
    },
    staleTime: 15 * 1000,
  });
};

export const useSandboxMetadataUpdate = () => {
  const queryClient = useQueryClient();
  const { data: orgId } = useUserOrgId();

  return useMutation({
    mutationFn: async ({ sandboxId, updates }) => {
      if (!sandboxId) throw new Error('sandboxId is required');
      const payload = await sendViewApiJson(`/api/ontology-views/sandboxes/${sandboxId}`, {
        method: 'PATCH',
        body: updates || {},
        meta: {
          query: 'sandbox-update',
          orgId,
          sandboxId,
        },
      });
      return payload?.sandbox || null;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [SANDBOX_QUERY_KEY] });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['resolvedViewDataBatch'] }),
        queryClient.invalidateQueries({ queryKey: ['resolvedViewDataSource'] }),
      ]);
    },
  });
};

export const useSandboxPublish = () => {
  const queryClient = useQueryClient();
  const { data: orgId } = useUserOrgId();

  return useMutation({
    mutationFn: async ({ sandboxId, revisionId, contentHash, dryRun = false }) => {
      if (!sandboxId) throw new Error('sandboxId is required');
      if (!revisionId || !contentHash) throw new Error('Load the exact revision before publishing.');
      const params = dryRun ? '?dry_run=true' : '';
      const payload = await sendViewApiJson(
        `/api/ontology-views/sandboxes/${encodeURIComponent(sandboxId)}/publish${params}`,
        {
          method: 'POST',
          body: { revision_id: revisionId, content_hash: contentHash },
          meta: {
            query: 'sandbox-publish',
            orgId,
            sandboxId,
          },
        }
      );
      return payload;
    },
    onError: async () => {
      await queryClient.invalidateQueries({ queryKey: [SANDBOX_QUERY_KEY] });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [SANDBOX_QUERY_KEY] });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [ONTOLOGY_VIEWS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: ['resolvedViewDataBatch'] }),
        queryClient.invalidateQueries({ queryKey: ['resolvedViewDataSource'] }),
        queryClient.invalidateQueries({ queryKey: ['ontologyViewRuntime'] }),
      ]);
    },
  });
};

export const useSandboxArchive = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ sandboxId }) => {
      if (!sandboxId) throw new Error('sandboxId is required');
      return sendViewApiJson(`/api/ontology-views/sandboxes/${encodeURIComponent(sandboxId)}/archive`, {
        method: 'POST',
        meta: { query: 'sandbox-archive', sandboxId },
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [SANDBOX_QUERY_KEY] });
    },
  });
};

export const useSandboxDelete = () => {
  const queryClient = useQueryClient();
  const { data: orgId } = useUserOrgId();

  return useMutation({
    mutationFn: async (sandboxId) => {
      if (!sandboxId) throw new Error('sandboxId is required');
      const payload = await sendViewApiJson(`/api/ontology-views/sandboxes/${sandboxId}`, {
        method: 'DELETE',
        meta: {
          query: 'sandbox-delete',
          orgId,
          sandboxId,
        },
      });
      return payload?.sandbox || null;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [SANDBOX_QUERY_KEY] });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [ONTOLOGY_VIEWS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: ['resolvedViewDataBatch'] }),
        queryClient.invalidateQueries({ queryKey: ['resolvedViewDataSource'] }),
      ]);
    },
  });
};

export const useViewActionInvoke = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      actionId,
      inputPayload,
      ontologyViewDefinitionId,
      interactionSchema,
      inputReason,
    }) => {
      if (!actionId) throw new Error('actionId is required');
      const startedAt = nowMs();
      logViewLatency('action.invoke.start', {
        actionId,
        ontologyViewDefinitionId,
        inputPayloadKeys: inputPayload && typeof inputPayload === 'object' ? Object.keys(inputPayload) : [],
      });
      const requestPayload = {
        input_payload: inputPayload ?? {},
        ontology_view_definition_id: ontologyViewDefinitionId ?? undefined,
        interaction_schema: interactionSchema && typeof interactionSchema === 'object' ? interactionSchema : {},
        input_reason: inputReason ?? undefined,
      };
      const extracted = extractActionPayloadFiles(requestPayload.input_payload);
      const hasFiles = extracted.files.length > 0;
      requestPayload.input_payload = extracted.payload ?? {};
      const requestOptions = hasFiles
        ? (() => {
            const formData = new FormData();
            formData.append('request_json', JSON.stringify(requestPayload));
            formData.append('file_field_paths', JSON.stringify(extracted.files.map((entry) => entry.path)));
            extracted.files.forEach((entry) => formData.append('files', entry.file, entry.file.name));
            return {
              method: 'POST',
              body: formData,
            };
          })()
        : {
            method: 'POST',
            body: JSON.stringify(requestPayload),
          };
      const endpoint = hasFiles
        ? `${admin_apiConfig.baseURL}/api/ontology-actions/${actionId}/invoke-with-files`
        : `${admin_apiConfig.baseURL}/api/ontology-actions/${actionId}/invoke`;
      const response = await authFetch(endpoint, requestOptions);
      if (!response.ok) {
        const err = await response.json().catch(() => ({ detail: response.statusText }));
        const apiError = buildApiError(err, `Action invocation failed (${response.status})`, response.status);
        logViewLatencyError('action.invoke.error', apiError, {
          actionId,
          ontologyViewDefinitionId,
          status: response.status,
          durationMs: elapsedMs(startedAt),
        });
        throw apiError;
      }
      const payload = takeDryRun(await response.json());
      const actionFailureMessage = getActionFailureMessage(payload);
      if (actionFailureMessage) {
        const apiError = buildApiError(
          {
            detail: {
              message: actionFailureMessage,
              output: payload?.output,
              orchestration_deliveries: payload?.orchestration_deliveries,
            },
          },
          'Action invocation failed.',
        );
        logViewLatencyError('action.invoke.error', apiError, {
          actionId,
          ontologyViewDefinitionId,
          status: response.status,
          durationMs: elapsedMs(startedAt),
        });
        throw apiError;
      }
      logViewLatency('action.invoke.success', {
        actionId,
        ontologyViewDefinitionId,
        status: response.status,
        durationMs: elapsedMs(startedAt),
        payloadKeys: payload && typeof payload === 'object' ? Object.keys(payload) : [],
      });
      return payload;
    },
    onSuccess: async (_data, variables) => {
      if (variables?.invalidateDefaultQueries === false) {
        return;
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [ONTOLOGY_VIEWS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: ['objectRelationships'] }),
        queryClient.invalidateQueries({ queryKey: ['objectRelationshipsTwoHop'] }),
        queryClient.invalidateQueries({ queryKey: ['resolvedViewDataBatch'] }),
        queryClient.invalidateQueries({ queryKey: ['resolvedViewDataSource'] }),
      ]);
    },
  });
};
